import { useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { fetchCurrentUser } from "../../service/CurrentUser";
import CfLoader from "../../common/CfLoader/CfLoader";
import { automationTestIds } from "../../testing/automationTestIds";
import AuthContext from "../../store/auth-context";
import { getRoleHomePath } from "../../routes/roleAccess";
import {
  ONBOARDING_PATH,
  shouldStartOnboarding,
} from "../../routes/onboardingAccess";

const sidebarComponentToRouteMap = {
  Dashboard: "/issuer/discover",
  Designs: "/designs",
  History: "/issuer/transaction/all",
  Draft: "/issuer/drafts",
  Approvals: "/issuer/approvals/all",
  Tenant: "/issuer/tenant",
  Finance: "/issuer/finance",
  Holders: "/issuer/holders",
  Settings: "/issuer/settings",
};

export const resolveSidebarRedirectPath = (userData) => {
  if (shouldStartOnboarding(userData, userData?.type || userData?.user_type)) {
    return ONBOARDING_PATH;
  }

  const sidebarComponents = userData?.sidebar_components || [];

  for (const sidebarItem of sidebarComponents) {
    if (
      sidebarItem === "Organisation" &&
      (userData.type === "Tenant_Admin" || userData.type === "Tenant_User") &&
      userData.tenant_code &&
      userData.tenant_code.trim() !== ""
    ) {
      return `/issuer/tenant/${userData.tenant_code}/organisations/users`;
    }

    if (sidebarComponentToRouteMap[sidebarItem]) {
      return sidebarComponentToRouteMap[sidebarItem];
    }
  }

  return getRoleHomePath(userData?.type || userData?.user_type);
};

const PostLoginRedirect = () => {
  const authContext = useContext(AuthContext);
  const [targetPath, setTargetPath] = useState(() =>
    getRoleHomePath(authContext.userType),
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const determineRedirectPath = async () => {
      try {
        if (authContext.sidebarComponents?.length > 0) {
          if (!cancelled) {
            setTargetPath(
              resolveSidebarRedirectPath({
                sidebar_components: authContext.sidebarComponents,
                type: authContext.userDetails?.type,
                user_type: authContext.userType,
                tenant_code:
                  authContext.tenantCode ||
                  authContext.userDetails?.tenant_code,
                is_new_user_onboarding: authContext.isNewUserOnboarding,
              }),
            );
          }
          return;
        }

        const userData = await fetchCurrentUser();
        if (!cancelled) {
          setTargetPath(
            userData
              ? resolveSidebarRedirectPath(userData)
              : getRoleHomePath(authContext.userType),
          );
        }
      } catch (error) {
        console.error("Error determining post-login redirect path:", error);
        if (!cancelled) {
          setTargetPath(getRoleHomePath(authContext.userType));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    determineRedirectPath();

    return () => {
      cancelled = true;
    };
    // Run once after auth bootstrap. PostLoginRedirect only mounts when
    // session loading is complete, so context values are already settled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return (
      <div
        className="w-full min-h-screen bg-cfSecondary flex items-center justify-center"
        id={automationTestIds.postLoginLoading}
      >
        <CfLoader />
      </div>
    );
  }

  return <Navigate to={targetPath} replace />;
};

export default PostLoginRedirect;
